import type {
  KeyAttributeSettingOptions,
  KeyAttributeTypesKeys,
  KeyMeasureTypesKeys,
  BuiltInDataSequenceSettingsTable,
  BuiltInDataTableSettings,
} from "building-types/dataSequences/scheduleSettingsTable";
import type { SystemDataSequenceEntityType } from "building-types/dataSequences/systemDataSequenceTypes";
import type { AddableAttributeNames, AddableMeasureNames } from "building-types/settings/measureSettingsTypes";
import { ensureDefined } from "common/utils/assertOfType";
import type { PartialRecord } from "common/utils/types";
import {
  DOORS_SCHEDULE_DEFAULT_SETTINGS,
  WINDOWS_SCHEDULE_DEFAULT_SETTINGS,
  ROOMS_SCHEDULE_DEFAULT_SETTINGS,
  CASEWORK_SCHEDULE_DEFAULT_SETTINGS,
} from "doc/defaults/measureSettingsScheduleDefaults";

export const SCHEDULE_SETTINGS_TABLE_DEFAULT: BuiltInDataSequenceSettingsTable = {
  DOOR: [DOORS_SCHEDULE_DEFAULT_SETTINGS],
  WINDOW: [WINDOWS_SCHEDULE_DEFAULT_SETTINGS],
  FLOORING: [ROOMS_SCHEDULE_DEFAULT_SETTINGS],
  CASEWORK: [CASEWORK_SCHEDULE_DEFAULT_SETTINGS],
};

export const DATA_TABLE_ATTRIBUTE_OVERRIDES: PartialRecord<
  SystemDataSequenceEntityType,
  Record<string, Partial<KeyAttributeSettingOptions>>
> = {
  WINDOW: {
    tag: { definesEntity: true, isHidden: false },
  },
  DOOR: {
    tag: { definesEntity: true, isHidden: false },
  },
};

export function attributesAddableToSetting(
  setting: BuiltInDataTableSettings,
  defaultSetting: BuiltInDataTableSettings,
): AddableAttributeNames[] {
  const result: AddableAttributeNames[] = [];
  // Add any attributes that don't exist in the current setting
  for (const attributeName in defaultSetting.attributes) {
    if (!setting.attributes[attributeName]) {
      const attribute = ensureDefined(defaultSetting.attributes[attributeName as KeyAttributeTypesKeys]);
      result.push({ attributeName: attributeName as KeyAttributeTypesKeys, displayName: attribute.displayName });
    }
  }
  // Add any attributes that are marked as not defining the entity
  for (const attributeName in setting.attributes) {
    const attribute = ensureDefined(setting.attributes[attributeName as KeyAttributeTypesKeys]);
    if (!attribute.definesEntity) {
      result.push({ attributeName: attributeName as KeyAttributeTypesKeys, displayName: attribute.displayName });
    }
  }
  return result;
}

export function measuresAddableToSetting(
  setting: BuiltInDataTableSettings,
  defaultSetting: BuiltInDataTableSettings,
): AddableMeasureNames[] {
  const result: AddableMeasureNames[] = [];
  // Add any measures that don't exist in the current setting
  for (const measureName in defaultSetting.measures) {
    if (!setting.measures[measureName]) {
      const measure = ensureDefined(defaultSetting.measures[measureName as KeyMeasureTypesKeys]);
      result.push({ measureName: measureName as KeyMeasureTypesKeys, displayName: measure.displayName });
    }
  }
  // Add any measures that are marked as not being aggregated
  for (const measureName in setting.measures) {
    const measure = ensureDefined(setting.measures[measureName as KeyMeasureTypesKeys]);
    if (!measure.isAggregated) {
      result.push({ measureName: measureName as KeyMeasureTypesKeys, displayName: measure.displayName });
    }
  }
  return result;
}
